import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Divider, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	ApplicationSummary,
	ApplicationTableDataType,
	Feedback,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useApplicationStatusTranslation } from '../../../hooks/useApplicationStatusTranslation';
import { useCallback, useState } from 'react';
import moment from 'moment';
import { useSnapshot } from 'valtio';
import { BatchInfoState } from '../../../components/BatchInfo.store';
import CheckTick from '../../../assets/images/green-tick.svg';
import CheckCross from '../../../assets/images/mobile-incomplete-check.svg';
import { ApplicationStatus, ReviewAction } from '../../../shared/Constants';
import NotApprovedCross from '../../../assets/images/not-approved.svg';
import { getFullName, isProposerSkippedToEnd } from '../../../shared/Utils';
import { useBatchId } from '../../../hooks/useBatchId';
import { PROPOSER_REASSIGN } from '../../../shared/Routes';
import { ZoneChangePopup } from './Zone-Change/ZoneChangePopup';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;

const InterviewNotStarted = ({
	otherReferenceProvided = false,
	statusValue,
}: {
	otherReferenceProvided?: boolean;
	statusValue?: string;
}) => {
	const { t } = useTranslation();
	const checkApproveAndNGFStatus = [
		ApplicationStatus.APPROVED,
		ApplicationStatus.REJECTED,
	].includes(statusValue as string);
	return (
		<>
			{(statusValue === ApplicationStatus.FUNCTIONARY_ASSIGNED ||
				checkApproveAndNGFStatus) && (
				<div className="flex flex-wrap gap-5">
					<div className="w-fit flex flex-row gap-2">
						<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
						<span className="font-semibold">{t('application')}</span>
					</div>
					<div className="w-fit flex flex-row gap-2">
						<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
						<span className="font-semibold">{t('candidate')}</span>
					</div>
					<div className="w-fit flex flex-row gap-2">
						<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
						<span className="font-semibold">{t('preceptor')}</span>
					</div>
					<div className="w-fit flex flex-row gap-2">
						<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
						<span className="font-semibold">{t('family')}</span>
					</div>
					{otherReferenceProvided && (
						<div className="w-fit flex flex-row gap-2">
							<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
							<span className="font-semibold">{t('otherReference')}</span>
						</div>
					)}
				</div>
			)}

			{(statusValue === ApplicationStatus.FUNCTIONARY_SUBMITTED ||
				checkApproveAndNGFStatus) && (
				<div className="flex flex-wrap gap-5">
					<div className="w-fit flex flex-row gap-2">
						<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
						<span className="font-semibold">{t('proposerRecommendation')}</span>
					</div>
					<div className="w-fit flex flex-row gap-2">
						<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
						<span className="font-semibold">
							{t('preceptorRecommendation')}
						</span>
					</div>
					<div className="w-fit flex flex-row gap-2">
						<img src={CheckCross} alt="Tick Logo" className="w-5 h-5" />
						<span className="font-semibold">{t('ZCRecommendation')}</span>
					</div>
				</div>
			)}
		</>
	);
};

const InterviewItem = ({
	completed,
	text,
	status,
}: {
	completed: boolean;
	text: string;
	status?: string;
}) => {
	const icon = completed ? (
		<img src={CheckTick} alt="Tick Logo" className="w-5 h-5" />
	) : (
		<img
			src={status === ReviewAction.REJECT ? NotApprovedCross : CheckCross}
			alt="Tick Logo"
			className="w-5 h-5"
		/>
	);

	return (
		<>
			<span>{icon}</span>
			<span className="font-semibold">{text}</span>
		</>
	);
};

const InterviewProgress = ({
	candidateInterviewCompleted = false,
	preceptorInterviewCompleted = false,
	familyMemberInterviewCompleted = false,
	otherReferenceInterviewCompleted = false,
	otherReferenceProvided = false,
	applicantAnswersReviewed = false,
	proposerRecommendation = {},
	preceptorRecommendation = {},
	zcRecommendation = {},
	careTeamRecommendation = {},
	applicationStatus,
	interviewType,
}: {
	applicantAnswersReviewed?: boolean;
	candidateInterviewCompleted?: boolean;
	preceptorInterviewCompleted?: boolean;
	familyMemberInterviewCompleted?: boolean;
	otherReferenceProvided?: boolean;
	otherReferenceInterviewCompleted?: boolean;
	proposerRecommendation?: Feedback;
	preceptorRecommendation?: Feedback;
	careTeamRecommendation?: Feedback;
	zcRecommendation?: Feedback;
	approverRecommendation?: Feedback;
	applicationStatus?: string;
	interviewType?: string;
}) => {
	const { t } = useTranslation();
	const checkApproveAndNGFStatus = [
		ApplicationStatus.APPROVED,
		ApplicationStatus.REJECTED,
	].includes(applicationStatus as string);

	if (
		(applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED ||
			checkApproveAndNGFStatus) &&
		interviewType === t('interviewProgress')
	) {
		if (
			!candidateInterviewCompleted &&
			!preceptorInterviewCompleted &&
			!familyMemberInterviewCompleted &&
			!otherReferenceInterviewCompleted &&
			!applicantAnswersReviewed
		) {
			return (
				<InterviewNotStarted
					statusValue={applicationStatus}
					otherReferenceProvided={otherReferenceProvided}
				/>
			);
		} else {
			return (
				<>
					<div className="flex flex-wrap gap-4">
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={applicantAnswersReviewed}
								text={t('application')}
							/>
						</div>
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={candidateInterviewCompleted}
								text={t('candidate')}
							/>
						</div>
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={preceptorInterviewCompleted}
								text={t('preceptor')}
							/>
						</div>
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={familyMemberInterviewCompleted}
								text={t('family')}
							/>
						</div>
						{otherReferenceProvided && (
							<div className="min-w-fit flex flex-row gap-2">
								<InterviewItem
									completed={otherReferenceInterviewCompleted}
									text={t('otherReference')}
								/>
							</div>
						)}
					</div>
				</>
			);
		}
	}

	if (
		(applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED ||
			checkApproveAndNGFStatus) &&
		interviewType === t('recommendation')
	) {
		const isPreceptorRecommendationApproved =
			preceptorRecommendation?.review === ReviewAction.APPROVE;
		const isProposerRecommendationApproved =
			proposerRecommendation?.review === ReviewAction.APPROVE;
		const isZcRecommendationApproved =
			zcRecommendation?.review === ReviewAction.APPROVE ||
			zcRecommendation?.review === ReviewAction.APPROVE_IDK_CANDIDATE;
		const isCareTeamecommendationApproved =
			careTeamRecommendation?.review === ReviewAction.APPROVE;

		const isProposerRecommendationDone =
			proposerRecommendation && Object.keys(proposerRecommendation).length > 0;

		const isPreceptorRecommendationDone =
			preceptorRecommendation &&
			Object.keys(preceptorRecommendation).length > 0;

		const isZcRecommendationDone =
			zcRecommendation && Object.keys(zcRecommendation).length > 0;

		const isCareTeamecommendationDone =
			careTeamRecommendation && Object.keys(careTeamRecommendation).length > 0;

		if (
			!isProposerRecommendationDone &&
			!isPreceptorRecommendationDone &&
			!isZcRecommendationDone &&
			!isCareTeamecommendationDone
		) {
			return <InterviewNotStarted statusValue={applicationStatus} />;
		} else {
			return (
				<>
					<div className="flex flex-wrap gap-4">
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={isPreceptorRecommendationApproved}
								text={t('preceptorRecommendation')}
								status={
									isProposerRecommendationDone
										? isPreceptorRecommendationApproved
											? ReviewAction.APPROVE
											: isProposerSkippedToEnd(preceptorRecommendation)
											? ReviewAction.NOT_APPLICABLE
											: ReviewAction.REJECT
										: ReviewAction.NOT_APPLICABLE
								}
							/>
						</div>
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={isProposerRecommendationApproved}
								text={t('proposerRecommendation')}
								status={
									isProposerRecommendationDone
										? isProposerRecommendationApproved
											? ReviewAction.APPROVE
											: isProposerSkippedToEnd(proposerRecommendation)
											? ReviewAction.NOT_APPLICABLE
											: ReviewAction.REJECT
										: ReviewAction.NOT_APPLICABLE
								}
							/>
						</div>
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={isZcRecommendationApproved}
								text={t('ZCRecommendation')}
								status={
									isZcRecommendationDone
										? isZcRecommendationApproved
											? ReviewAction.APPROVE
											: isProposerSkippedToEnd(zcRecommendation)
											? ReviewAction.NOT_APPLICABLE
											: ReviewAction.REJECT
										: ReviewAction.NOT_APPLICABLE
								}
							/>
						</div>
						<div className="min-w-fit flex flex-row gap-2">
							<InterviewItem
								completed={isCareTeamecommendationApproved}
								text={t('careTeamRecommendation')}
								status={
									isCareTeamecommendationDone
										? isCareTeamecommendationApproved
											? ReviewAction.APPROVE
											: isProposerSkippedToEnd(careTeamRecommendation)
											? ReviewAction.NOT_APPLICABLE
											: ReviewAction.REJECT
										: ReviewAction.NOT_APPLICABLE
								}
							/>
						</div>
					</div>
				</>
			);
		}
	}

	return null;
};

interface ApplicationSummaryCardProps extends ApplicationSummary {
	onEvaluateClick: (application: ApplicationSummary) => void;
	onVerifyResultsClick: (application: ApplicationSummary) => void;
	isOverAllCandidatesStatus: boolean;
}

export const DashboardSummaryCard = (props: ApplicationSummaryCardProps) => {
	const {
		applicant: { firstName, lastName, srcmid, gender },
		center,
		applicationId,
		applicationStatus,
		proposerName,
		proposerSrcmId,
		applicantAnswersReviewed,
		familyMemberInterviewDone,
		applicantInterviewDone,
		preceptorInterviewDone,
		otherReferenceInterviewDone,
		otherReferenceProvided,
		onEvaluateClick,
		onVerifyResultsClick,
		proposerRecommendation,
		preceptorRecommendation,
		zcRecommendation,
		careTeamRecommendation,
		isOverAllCandidatesStatus,
	} = props;
	const { t, i18n } = useTranslation();
	const isLengthyLanguage = i18n.language === 'ka';
	const { getTranslatedValue } = useApplicationStatusTranslation();
	const { selectedRole } = useUserSession();
	const isScreeningAdmin = selectedRole === UserRole.SCREENING_ADMIN;
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;
	const { navigateToRoute } = useBatchId();
	const batchStateSnap = useSnapshot(BatchInfoState);
	const batchInfo = batchStateSnap.batchInfo;
	const [isZoneChangeModalOpen, setIsZoneChangeModalOpen] = useState(false);
	const [zoneChangeApplication, setZoneChangeApplication] =
		useState<ApplicationTableDataType>();
	const handleEvaluateClick = useCallback(() => {
		onEvaluateClick(props);
	}, [onEvaluateClick, props]);
	const handleVerifyResultsClick = useCallback(() => {
		onVerifyResultsClick(props);
	}, [onVerifyResultsClick, props]);
	const handleZoneChangeModalOpen = () => {
		setIsZoneChangeModalOpen(true);
	};

	const handleZoneChangeModalClose = () => {
		setIsZoneChangeModalOpen(false);
	};

	const handleZoneChangeClick = (application: ApplicationSummaryCardProps) => {
		const applicationProps: ApplicationTableDataType = {
			fullName: getFullName(application.applicant),
			idCard: application.applicant.srcmid,
			gender: application.applicant.gender,
			proposerName: application.proposerName,
			proposerSRCMID: application.proposerSrcmId
				? application.proposerSrcmId
				: '',
			center: application.center.name,
			zone: application.center.zone,
			originalApplication: application,
		};
		setZoneChangeApplication(applicationProps);
		handleZoneChangeModalOpen();
	};
	const showVerifyResults =
		(selectedRole === UserRole.ZC &&
			batchInfo?.snpResultDateForZC &&
			batchInfo.snpResultDateForZC !== '1970-01-01T00:00:00Z' &&
			moment().isSameOrAfter(batchInfo?.snpResultDateForZC)) ||
		(selectedRole === UserRole.APPROVER &&
			batchInfo?.snpResultDateForApprover &&
			batchInfo.snpResultDateForApprover !== '1970-01-01T00:00:00Z' &&
			moment().isSameOrAfter(batchInfo?.snpResultDateForApprover));
	const showInterviewAndRecommendationProgress = [
		ApplicationStatus.APPROVED,
		ApplicationStatus.REJECTED,
	].includes(applicationStatus as string);
	const showInterviewProgress =
		selectedRole !== UserRole.ZC &&
		selectedRole !== UserRole.CARE_TEAM &&
		(applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED ||
			applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED ||
			showInterviewAndRecommendationProgress);

	const handleProposerReassign = useCallback(() => {
		const applicationData = {
			applicant: { firstName, lastName, srcmid, gender },
			center,
			applicationId,
			applicationStatus,
			proposerName,
			proposerSrcmId,
		};
		navigateToRoute(PROPOSER_REASSIGN, {
			state: {
				applicationRecord: applicationData,
			},
		});
		sessionStorage.setItem('screeningAdmin', 'Screening Admin');
	}, [
		firstName,
		lastName,
		srcmid,
		gender,
		center,
		applicationId,
		applicationStatus,
		proposerName,
		proposerSrcmId,
		navigateToRoute,
	]);

	return (
		<>
			<div className="flex flex-col rounded-lg border border-solid border-gray-300 bg-[rgba(232,232,233,0.4)] p-[16px] py-[24px]">
				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('fullName')}</Text>
						<Text className="text-gray-650 text-sm font-bold">{`${
							firstName ?? ''
						} ${lastName ?? ''}`}</Text>
					</div>
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">
							{t('applicationStatus')}
						</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{getTranslatedValue(applicationStatus)}
						</Text>
					</div>
				</div>

				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('idCardNo')}</Text>
						<Text className="text-gray-650 text-sm font-bold">{srcmid}</Text>
					</div>
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('gender')}</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{gender &&
								gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()}
						</Text>
					</div>
				</div>

				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('center')}</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{center.name}
						</Text>
					</div>
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('zone')}</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{center.zone}
						</Text>
					</div>
				</div>

				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						{selectedRole !== UserRole.CARE_TEAM && (
							<>
								<Text className="text-light-gray text-sm">
									{t('proposerName')}
								</Text>
								<Text className="text-gray-650 text-sm font-bold">
									{proposerName}
								</Text>
							</>
						)}
					</div>
					<div className="flex flex-col gap-1 w-[50%]">
						{selectedRole !== UserRole.CARE_TEAM && (
							<>
								<Text className="text-light-gray text-sm">
									{t('proposerSRCMID')}
								</Text>
								<Text className="text-gray-650 text-sm font-bold">
									{proposerSrcmId}
								</Text>
							</>
						)}
					</div>
				</div>

				{showInterviewProgress && !isOverAllCandidatesStatus && (
					<>
						{(applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED ||
							showInterviewAndRecommendationProgress) && (
							<>
								<Divider className="!border-t-2 !border-gray-200" />
								<Text className="text-secondary-black text-base font-sans font-semibold text-center pb-6">
									{t('interviewProgress')}
								</Text>
								<InterviewProgress
									applicationStatus={applicationStatus}
									applicantAnswersReviewed={applicantAnswersReviewed}
									candidateInterviewCompleted={applicantInterviewDone}
									preceptorInterviewCompleted={preceptorInterviewDone}
									familyMemberInterviewCompleted={familyMemberInterviewDone}
									otherReferenceInterviewCompleted={otherReferenceInterviewDone}
									otherReferenceProvided={otherReferenceProvided}
									interviewType={t('interviewProgress') ?? ''}
								/>
							</>
						)}

						{(applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED ||
							showInterviewAndRecommendationProgress) && (
							<>
								<Divider className="!border-t-2 !border-gray-300" />
								<Text className="text-secondary-black text-base font-sans font-semibold text-center pb-6">
									{t('recommendation')}
								</Text>
								<InterviewProgress
									applicationStatus={applicationStatus}
									preceptorRecommendation={preceptorRecommendation}
									proposerRecommendation={proposerRecommendation}
									zcRecommendation={zcRecommendation}
									careTeamRecommendation={careTeamRecommendation}
									interviewType={t('recommendation') ?? ''}
								/>
							</>
						)}
					</>
				)}

				<span className="border-t border-gray-300 opacity-0 py-2">
					Hidden Text
				</span>
				{showVerifyResults === true ? (
					<div className="flex justify-center">
						<Button
							type="primary"
							onClick={handleVerifyResultsClick}
							className={`${isLengthyLanguage ? 'w-fit' : 'w-[50%]'}`}
						>
							<span>
								{t('verifyResults')} <ArrowRightOutlined />
							</span>
						</Button>
					</div>
				) : (
					<div className="flex flex-col gap-4 justify-center">
						{!isScreeningAdmin && (
							<Button
								type="primary"
								onClick={handleEvaluateClick}
								className={`w-full`}
							>
								<span>
									{t('view')} <ArrowRightOutlined />
								</span>
							</Button>
						)}
						{(isScreeningAdmin || isAdmin) &&
							((isScreeningAdmin &&
								(applicationStatus === 'USER_SUBMITTED_APPLICATION' ||
									applicationStatus === 'FUNCTIONARY_ASSIGNED' ||
									applicationStatus === 'FUNCTIONARY_SUBMITTED')) ||
								isAdmin) && (
								<Button
									className={`font-sans font-bold text-sm border border-solid mt-0.5 text-primary-blue border-primary-blue w-full bg-transparent ${
										((isScreeningAdmin || isAdmin) &&
											applicationStatus === 'FUNCTIONARY_SUBMITTED') ||
										applicationStatus === 'WAITING_FOR_CARE_TEAM' ||
										applicationStatus === 'APPROVED' ||
										applicationStatus === 'REJECTED'
											? 'opacity-50 cursor-not-allowed pointer-events-none'
											: ''
									}`}
									variant="outlined"
									onClick={() => {
										if (
											(isScreeningAdmin || isAdmin) &&
											(applicationStatus === 'FUNCTIONARY_ASSIGNED' ||
												applicationStatus === 'USER_SUBMITTED_APPLICATION')
										) {
											handleProposerReassign();
										}
									}}
								>
									{t('proposerReassign')}
								</Button>
							)}
						{(isScreeningAdmin || isAdmin) && (
							<Button
								className={`font-sans font-bold text-sm border border-solid mt-0.5 text-primary-blue border-primary-blue w-full bg-transparent`}
								variant="outlined"
								onClick={() => handleZoneChangeClick(props)}
							>
								{t('zoneChange')}
							</Button>
						)}
					</div>
				)}
			</div>
			<ZoneChangePopup
				open={isZoneChangeModalOpen}
				onClose={handleZoneChangeModalClose}
				application={zoneChangeApplication}
			/>
		</>
	);
};
